import 'mapbox-gl/dist/mapbox-gl.css';

import jammingTile_512x512_url from '@assets/jammingTile_512x512.png';
import { HStack } from '@chakra-ui/react';
import { ContextMenu } from '@components/map/contextMenu/ContextMenu';
import CoordinatesControl from '@components/map/controls/CoordinatesControl';
import ScaleControl from '@components/map/controls/ScaleControl';
import { MeasureLayer } from '@components/map/layers/draw/measure/MeasureLayer';
import { useMapContext } from '@components/map/MapContext';
import { MapInfoPicker } from '@components/map/popup/MapInfoPicker';
import { Popups } from '@components/map/popup/Popups';
import { useAppSelector } from '@hooks/redux.hooks';
import { useCloseLastOpenedPopup } from '@hooks/useCloseLastOpenedPopup';
import { useSelectorWithMapId } from '@hooks/useSelectorWithMapId';
import { useWithDispatch } from '@hooks/useWithDispatch';
import { useZoneFormPopup } from '@hooks/useZoneFormPopup';
import { selectBaseLayer, selectMapLayersByIds } from '@redux/config/config.selectors';
import { closeAllPopupsByMapId, hidePerimeterLinksByMapId } from '@redux/maps/maps.reducer';
import { selectMapDrawModeByMapId, selectMapDrawPolygonsPoints } from '@redux/maps/maps.selectors';
import { clearMapCommand } from '@redux/maps/mapsCommands.reducer';
import { selectMapCommandByMapId } from '@redux/maps/mapsCommands.selectors';
import { selectLanguage, selectOverlaysKey } from '@redux/settings/settings.selectors';
import { getSettingsLocale } from '@utils/locale.utils';
import { isBaseStyleRaster } from '@utils/map/map.utils';
import { MapEvent, MapLayerMouseEvent, RequestParameters, ResourceType, TransformRequestFunction } from 'mapbox-gl';
import { PropsWithChildren, ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AttributionControlProps,
  Layer,
  Map,
  MapRef,
  Source,
  StyleSpecification,
  ViewState,
} from 'react-map-gl/mapbox';

import { BaseStyleRaster, LayerNameEnum, SourceNameEnum } from '@/types/map.types';

import MapTitle from './title/MapTitle';
import Tooltip from './tooltips/Tooltip';

const MAPBOX_ACCESS_TOKEN = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;
const BASE_BACKGROUND_MAP_URL = import.meta.env.VITE_BASE_BACKGROUND_MAP_URL;
const DEV = import.meta.env.DEV;

export interface MapboxMapProps {
  attribution?: ReactElement<AttributionControlProps>;
  navigation?: ReactElement;
  showScale?: boolean;
  zoom?: ReactElement;
}

export default function MapboxMap({
  // attribution = <AttributionControl />,
  showScale = true,
  children,
}: PropsWithChildren<MapboxMapProps>) {
  const {
    mapId,
    isSelectionEnabled,
    infoLayers,
    isMeasureActive,
    viewMode,
    isNorthLocked,
    viewState,
    setViewState,
    setMap,
  } = useMapContext();
  const mapRef = useRef<MapRef>(null);
  const language = getSettingsLocale(useAppSelector(selectLanguage));

  const clearCommand = useWithDispatch(clearMapCommand);
  const mapCommand = useAppSelector((state) => selectMapCommandByMapId(state, mapId));

  const mapStyle: StyleSpecification | BaseStyleRaster | undefined = useAppSelector(selectBaseLayer);

  const currentOverlaysIds = useAppSelector(selectOverlaysKey);
  const overlays = useAppSelector((state) => selectMapLayersByIds(state, currentOverlaysIds));

  // handle drawing side effects
  const drawMode = useSelectorWithMapId(selectMapDrawModeByMapId);
  const drawnPoints = useSelectorWithMapId(selectMapDrawPolygonsPoints);

  const [cursor, setCursor] = useState<string>('auto');

  const hidePerimeterLinks = useWithDispatch(hidePerimeterLinksByMapId);
  const closeAllPopups = useWithDispatch(closeAllPopupsByMapId);

  const interactiveLayers = useMemo(
    () => [...infoLayers, LayerNameEnum.SENSOR_CLUSTERS, LayerNameEnum.SENSOR_CLUSTER_LEAVES],
    [infoLayers],
  );

  const [mapKey, setMapKey] = useState(0);

  useZoneFormPopup();
  useCloseLastOpenedPopup(mapId);
  useEffect(() => {
    if ((drawMode && drawnPoints && drawnPoints.coordinates[0].length < 3) || isSelectionEnabled) {
      setCursor('crosshair');
    } else {
      setCursor('auto');
    }
  }, [drawMode, drawnPoints, isSelectionEnabled]);

  const handleMove = useCallback(
    (event: { viewState: unknown }) => {
      setViewState(event.viewState as ViewState);
    },
    [setViewState],
  );

  // Transform sprite source because mapbox only accept url
  function handleTransformRequest(url: string, resourceType: ResourceType): RequestParameters | undefined {
    if (resourceType === 'Tile') {
      if (url.includes('${BASE_URL}')) {
        return {
          url: url.replace('${BASE_URL}', DEV ? BASE_BACKGROUND_MAP_URL : `${window['location'].origin}`),
          headers: {
            'Cache-Control': 'no-cache',
            Pragma: 'no-cache',
          },
        } as RequestParameters;
      }
      return { url: url };
    } else if (resourceType === 'SpriteJSON' || resourceType === 'SpriteImage' || resourceType === 'Glyphs') {
      return {
        url: url.replace('data:/', '').concat(`?=v${__APP_VERSION__}`),
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      } as RequestParameters;
    }
  }

  function handleClick(e: MapLayerMouseEvent) {
    hidePerimeterLinks(mapId);
    if (e.features?.length === 0 && !isMeasureActive && !drawMode && !isSelectionEnabled) {
      closeAllPopups(mapId);
    }
  }

  function handleContextMenu() {
    hidePerimeterLinks(mapId);
  }

  const onMouseEnter = useCallback(() => setCursor('pointer'), []);
  const onMouseLeave = useCallback(() => setCursor('auto'), []);

  const handleLoad = (e: MapEvent) => {
    const map = mapRef.current?.getMap();
    if (!map) return;

    setMap(e.target);
    const canvas = map.getCanvas();

    canvas.addEventListener('webglcontextlost', (evt) => {
      evt.preventDefault();
      setMapKey((k) => k + 1);
    });

    if (mapRef.current) {
      mapRef.current.loadImage(jammingTile_512x512_url, (error, image) => {
        if (error) throw error;
        if (!mapRef.current!.hasImage('jammingTile_512x512') && image) {
          mapRef.current!.addImage('jammingTile_512x512', image, { pixelRatio: 2 });
        }
      });
    }
  };

  const transformStringMapStyle = (url: string) => {
    if (url.includes('http')) return url;
    return `${DEV ? BASE_BACKGROUND_MAP_URL : window.location.origin}${url}`;
  };

  useEffect(() => {
    if (mapRef.current && mapCommand !== undefined) {
      mapRef.current.flyTo({
        center: [mapCommand.payload.center.longitude, mapCommand.payload.center.latitude],
        zoom: mapCommand.payload.zoom,
      });
      clearCommand({ mapId });
    }
  }, [clearCommand, mapCommand, mapId]);

  return (
    <Map
      {...viewState}
      key={mapKey}
      ref={mapRef}
      id={mapId}
      mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
      dragRotate={viewMode === '3D' || !isNorthLocked}
      style={{ width: '100%', height: '100%', userSelect: 'none' }}
      mapStyle={isBaseStyleRaster(mapStyle) ? mapStyle.baseStyle : mapStyle}
      language={language}
      attributionControl={false}
      transformRequest={handleTransformRequest as TransformRequestFunction}
      terrain={{
        source: 'raster-dem',
        exaggeration: 0.5,
      }}
      cursor={cursor}
      interactiveLayerIds={interactiveLayers}
      onMove={handleMove}
      onClick={handleClick}
      onContextMenu={handleContextMenu}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onLoad={handleLoad}
      minZoom={0}
      maxZoom={22}
      preserveDrawingBuffer
    >
      <>
        {/*attribution*/}
        <MapTitle />

        {isBaseStyleRaster(mapStyle) && (
          <Source
            id={SourceNameEnum.BASE_LAYER}
            type="raster"
            tiles={[transformStringMapStyle(mapStyle.url)]}
            tileSize={256}
          >
            <Layer id={LayerNameEnum.BASE_LAYER} type="raster" />
          </Source>
        )}

        {overlays
          .filter((overlay) => overlay.url)
          .map((overlayLayer) => (
            <Source
              key={overlayLayer.id}
              id={`mesh-source-${overlayLayer.id}`}
              type="raster"
              tiles={[overlayLayer.url!]}
              tileSize={256}
            >
              <Layer id={`mesh-layer-${overlayLayer.id}`} type="raster" />
            </Source>
          ))}

        {children}

        <HStack
          gap={2}
          position="absolute"
          flex={1}
          right={0}
          bottom={0}
          fontFamily="roboto"
          textStyle="sm"
          height="40px"
          margin={2}
          alignItems="stretch"
          userSelect="none"
        >
          {showScale && <ScaleControl zoom={viewState.zoom} />}
          <CoordinatesControl viewState={viewState} />
        </HStack>
        <Tooltip />
        <Popups />
        <ContextMenu />
        <MapInfoPicker />
        <MeasureLayer />
      </>
    </Map>
  );
}
